import { FormControl, FormHelperText, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { BlueBankIcon, BlueCardIcon } from '~/modules/@shared/components';
import { useMemberPaymentMethods } from '~/modules/contact/hooks/useMemberPaymentMethods';
import { TakePaymentFormData } from '~/modules/payment/components/TakePayment/helpers/TakePaymentSchema';

type Props = {
  paymentMethods: ReturnType<typeof useMemberPaymentMethods>['paymentMethods'];
  addAutomaticPaymentOnDueDateOption?: boolean;
  onOpenPaymentMethodsModal: () => void;
};

export default function MemberPaymentMethod({
  paymentMethods,
  onOpenPaymentMethodsModal,
  addAutomaticPaymentOnDueDateOption,
}: Props) {
  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<TakePaymentFormData>();

  const memberId = watch('member_id');

  const defaultPaymentMethodId = useMemo(() => {
    if (paymentMethods?.length > 0) {
      return paymentMethods.find((paymentMethod) => paymentMethod.default)?.id;
    }
    return undefined;
  }, [paymentMethods]);

  const handleChange = (event: SelectChangeEvent<number | string>, onChange: (value: number | string) => void) => {
    const value = event.target.value;
    if (value === undefined) {
      event.preventDefault();
      onOpenPaymentMethodsModal();
    } else {
      onChange(value);
    }
  };

  useEffect(() => {
    if (defaultPaymentMethodId) {
      setValue('payment_method_id', defaultPaymentMethodId);
    } else {
      setValue('payment_method_id', 0);
    }
  }, [setValue, defaultPaymentMethodId]);

  return (
    <Controller
      control={control}
      name="payment_method_id"
      render={({ field: { value, onChange, ...field } }) => (
        <FormControl fullWidth>
          <Select
            {...field}
            onChange={(e) => handleChange(e, onChange)}
            displayEmpty
            value={value ?? ''}
            error={!!errors.payment_method_id}>
            <MenuItem>+ Add New Payment Method</MenuItem>
            {memberId
              ? paymentMethods.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.type === 'cc' ? (
                      <BlueCardIcon
                        style={{
                          marginRight: '0.5em',
                        }}
                      />
                    ) : (
                      <BlueBankIcon
                        style={{
                          marginRight: '0.5em',
                        }}
                      />
                    )}
                    {item.number}
                  </MenuItem>
                ))
              : []}
            <MenuItem key="cash" value={0}>
              Cash
            </MenuItem>
            {addAutomaticPaymentOnDueDateOption && (
              <MenuItem key="automatic_charge" value="automatic_charge" disabled={!defaultPaymentMethodId}>
                Automatic Payment on Due Date
              </MenuItem>
            )}
          </Select>
          {!!errors.payment_method_id && <FormHelperText error>{`${errors.payment_method_id.message}`}</FormHelperText>}
        </FormControl>
      )}
    />
  );
}
